<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>{{ $t("profile.information.title") }}</template>

        <template #description>{{
            $t("profile.information.description")
        }}</template>

        <template #form>
            <!-- Profile Photo -->
            <div
                v-if="$page.props.jetstream.managesProfilePhotos"
                class="col-span-6 sm:col-span-4"
            >
                <!-- Profile Photo File Input -->
                <input
                    ref="photo"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                />

                <jet-label
                    for="photo"
                    :value="$t('profile.information.photo')"
                />

                <div class="flex items-center gap-4">
                    <!-- Current Profile Photo -->
                    <div v-show="!photoPreview" class="mt-2 shrink-0">
                        <img
                            :src="user.profile_photo_url"
                            :alt="user.name"
                            class="h-20 w-20 rounded-full object-cover"
                        />
                    </div>

                    <!-- New Profile Photo Preview -->
                    <div v-show="photoPreview" class="mt-2 shrink-0">
                        <span
                            class="block h-20 w-20 rounded-full"
                            :style="
                                'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' +
                                photoPreview +
                                '\');'
                            "
                        ></span>
                    </div>

                    <div class="">
                        <Button
                            variant="outline"
                            class="mr-2"
                            type="button"
                            @click.prevent="selectNewPhoto"
                        >
                            <Upload :size="20" class="mr-2"></Upload>
                            {{ $t("profile.information.upload_image") }}</Button
                        >

                        <Button
                            v-if="user.profile_photo_path"
                            class="mt-1 block"
                            variant="ghost"
                            @click.prevent="deletePhoto"
                        >
                            {{ $t("profile.information.remove_photo") }}
                        </Button>
                    </div>
                </div>
            </div>

            <div class="col-span-6 sm:col-span-4">
                <div class="mt-2 flex items-center space-x-2">
                    <Switch
                        id="use-in-emailing"
                        :checked="useProfileImageInEmailing"
                        @update:checked="toggleUseInEmailing"
                    />
                    <Label for="use-in-emailing">{{
                        $t("profile.use_profile_image_in_emailing")
                    }}</Label>
                    <PaidFeatureIcon />
                </div>
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="name" :value="$t('profile.information.name')" />
                <jet-input
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="mt-1 block w-full"
                    autocomplete="name"
                />
                <jet-input-error :message="form.errors.name" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label
                    for="email"
                    :value="$t('profile.information.email')"
                />
                <jet-input
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                />
                <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>
        </template>

        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">{{
                $t("saved")
            }}</jet-action-message>

            <jet-button
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing"
                >{{ $t("save") }}</jet-button
            >
        </template>
    </jet-form-section>
</template>

<script lang="ts" setup>
import JetButton from "../../Jetstream/Button.vue";
//@ts-expect-error
import JetFormSection from "../../Jetstream/FormSection.vue";
//@ts-expect-error
import JetInput from "../../Jetstream/Input.vue";
//@ts-expect-error
import JetInputError from "../../Jetstream/InputError.vue";
//@ts-expect-error
import JetLabel from "../../Jetstream/Label.vue";
//@ts-expect-error
import JetActionMessage from "../../Jetstream/ActionMessage.vue";
//@ts-expect-error
import JetSecondaryButton from "../../Jetstream/SecondaryButton.vue";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-vue-next";
import { router, useForm, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, ref, watch } from "vue";
import useConfirm from "../ZapTime/App/ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import PaidFeatureIcon from "../ZapTime/App/Atomic/PaidFeatureIcon.vue";

import useProDialog from "@/ProModal/composables/useProDialog";
import { useHasSubscription } from "@/composables/useHasSubscription";

const props = defineProps<{
    user: any;
}>();

const { show } = useConfirm();
const { t } = useI18n();
const photo = ref();

const form = useForm({
    name: props.user.name,
    email: props.user.email,
});

const photoForm = useForm({
    image: null,
    useWithEventTypes: false,
});

type SharedData = {
    personalTeam: {
        id: number;
        use_profile_image_in_emailing: boolean;
    };
};

const photoPreview = ref(null);
const page = usePage<SharedData>();

const { hasSubscription } = useHasSubscription();
const proDialog = useProDialog();

const useProfileImageInEmailing = computed(() => {
    return page.props.personalTeam.use_profile_image_in_emailing;
});

function updateProfileInformation() {
    form.put(route("user-profile-information.update"), {
        errorBag: "updateProfileInformation",
        preserveScroll: true,
    });
}

function toggleUseInEmailing() {
    if (!hasSubscription) {
        proDialog.show({
            feature: "customize_confirmation_email",
        });
    } else {
        router.post(
            route("app.teams.profile-photos.use-in-emailing", {
                team: page.props.personalTeam.id,
            }),
        );
    }
}

async function updateProfilePhoto() {
    if (photo.value) {
        photoForm.image = photo.value.files[0];
    }

    // Show a confirmation where user decides if they want to update all event types with the new photo
    const res = await show({
        type: "info",
        header: t("profile.information.update.header"),
        message: t("profile.information.update.message"),
        confirm: t("profile.information.update.confirm"),
        cancel: t("profile.information.update.cancel"),
    });

    if (res) {
        photoForm.useWithEventTypes = true;
    }

    photoForm.post(route("app.profile-photos.store"), {
        errorBag: "updateProfileInformation",
        preserveScroll: true,
    });
}

function selectNewPhoto() {
    photo.value.click();
}

function updatePhotoPreview() {
    const reader = new FileReader();

    reader.onload = (e) => {
        //@ts-expect-error
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo.value.files[0]);
    updateProfilePhoto();
}

function deletePhoto() {
    photoForm.delete(route("app.profile-photos.destroy"), {
        preserveScroll: true,
        onSuccess: () => (photoPreview.value = null),
    });
}
</script>
